import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import * as R from 'ramda';
import { css } from '@emotion/core';
import { Str } from '../../../localization/translationKeys';
import { FLEX_COLUMN, FLEX_ROW } from '../../../layout/flexBox';
import { useMsgText } from '../../../localization/hooks';
import { COLORS } from '../../../globalStyle/colors';
import { Map } from '../../../language/models';
import { DevPlanFieldAndAnswer } from '../models';
import LocalDiv from '../../../localization/LocalDiv';
import LocalBtn from '../../../localization/LocalBtn';
import { btnStyle } from '../../../forms/styles/button';
import Spacer from '../../../layout/Spacer';
import { useDispatch } from 'react-redux';
import { calcUpdateFieldAnswerApiState, dispatchFieldAnswerUpdates } from './module';
import { useAppState } from '../../../core/redux/hooks';
import DevActionListField from '../listItems/DevActionListField';
import DevActionField from '../DevActionField';
import { TEXT_AREA } from '../../../forms/styles/textarea';


const styles = {
  root:        css`
    transition: opacity .5s ease;
  `,
  loadingForm: css`
    opacity: .4;
  `,
  clickable:   css`
    cursor: pointer;
  `,
  inputGroup:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      margin-bottom: 35px;
    `,
  ],
  label:       css`
    color: ${COLORS.DARK_GREY};
    font-size: 12px;
    line-height: 15px;
    font-weight: bold;
  `,
  textArea:    css`
    width: 100%;
    background-color: ${COLORS.WHITE};
    font-size: 13px;
    ::placeholder {
      font-style: italic;
    }
  `,
  btnRow:      [
    FLEX_ROW,
    css`
      transition: all .5s ease;
      overflow: hidden;
    `,
  ],
  open:        css`
    padding-top: 10px;
    height: 35px;
    opacity: 1;
  `,
  closed:      css`
    height: 0;
    opacity: 0;
  `,
  saveBtn:     [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 80px;
    `,
  ],
  cancelBtn:   [
    btnStyle.tertiary(COLORS.BLUE),
    css`
      min-width: 80px;
    `,
  ],
};


/**
 * Returns the number of new lines in the given maybe string. If the maybe string isn't a string, then you get back 1.
 */
const calcLines = R.memoizeWith(
  (s: string | undefined): string => `${s}`,
  (s: string | undefined): number => s?.split('\n')?.length ?? 1,
);


type Props = {
  fieldsAnswers: DevPlanFieldAndAnswer[];
  devItemCompetencyId?: number;
  isParticipant: boolean;
  className?: string;
};


/**
 * DevActionForm: a component that takes a list of fields and answers and allows the user to view/edit them. This
 * component handles saving changes to the API.
 *
 * PureComponent: true
 */
const DevActionForm: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const placeholder = useMsgText(Str.ResponseNotAdded);

  const [dirty, setDirtyObj]     = useState<Map<string>>({});
  const [editing, setEditingObj] = useState<Map<boolean>>({});
  const isEditing                = useMemo(() => !!Object.keys(editing).length, [editing]);

  const getLoading = useCallback(calcUpdateFieldAnswerApiState(props.fieldsAnswers), [props.fieldsAnswers]);
  const loading    = useAppState(state => getLoading(state.loading));

  const reset = useCallback(
    () => {
      setDirtyObj({});
      setEditingObj({});
    },
    [setDirtyObj, setEditingObj],
  );

  const setEditing = useCallback(
    (key: string) => {
      if (loading) {
        return;
      }

      setEditingObj(R.assoc(key, true, editing));

      // Not all browsers properly focus the text area, and some even override focus for the click event.
      // This line: "After the event is processed, set focus on the clicked text area"
      setTimeout(() => document.getElementById(`${key}`)?.focus(), 0);
    },
    [loading, editing, setEditingObj],
  );

  const setDirty = useCallback(
    (key: string, value: string | undefined) => setDirtyObj(R.assoc(key, value, dirty)),
    [dirty, setDirtyObj],
  );

  const onCancel = useCallback(
    event => {
      event.preventDefault();
      reset();
    },
    [reset],
  );

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (loading) {
        return;
      }

      dispatchFieldAnswerUpdates(dispatch, props.fieldsAnswers, dirty);

      reset();
    },
    [loading, props, dirty, reset, dispatch],
  );

  return (
    <form
      onReset={onCancel}
      onSubmit={onSubmit}
      css={[styles.root, loading && styles.loadingForm]}
      className={props.className}
    >
      { // Questions / Answers
        props.fieldsAnswers.map((item, i) => item.list
          ? (
            <DevActionListField
              field={item}
              devItemCompetencyId={props.devItemCompetencyId}
              placeholder={placeholder}
              disableModal={!props.isParticipant}
              css={[styles.inputGroup]}
            />
          )
          : (
            <DevActionField
              key={item.question}
              item={item}
              placeholder={placeholder}
              rows={calcLines(item.answer)}
              value={dirty[`${item.devPlanFieldId}`] ?? item.answer ?? ''}
              onClick={() => setEditing(`${item.devPlanFieldId}`)}
              onChangeAnswer={event => setDirty(`${item.devPlanFieldId}`, event.target.value)}
              disableTextArea={!props.isParticipant || !editing[`${item.devPlanFieldId}`]}
              textAreaStyle={[TEXT_AREA, styles.textArea, !editing[`${item.devPlanFieldId}`] && styles.clickable]}
              css={[
                styles.inputGroup,
                !editing[`${item.devPlanFieldId}`] && styles.clickable,
                i === props.fieldsAnswers.length - 1 && css`margin-bottom: 0;`,
              ]}
            />
          ),
        )
      }

      {
        /* Submit/Cancel */
        props.isParticipant
        && <LocalDiv css={[styles.btnRow, isEditing ? styles.open : styles.closed]}>
             <LocalBtn type='submit' str={Str.Save} css={styles.saveBtn} />
             <Spacer sizePx={5} />
             <LocalBtn type='reset' str={Str.Cancel} css={styles.cancelBtn} />
           </LocalDiv>
      }
    </form>
  );
};

export default DevActionForm;
